import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, RotateCcw, Loader2, Plus, Trash2, Image as ImageIcon } from "lucide-react";
import { authApi } from "../auth/AuthContext";
import { useContent } from "../cms/ContentContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

export default function SiteEditor() {
  const { content, refresh } = useContent();
  const navigate = useNavigate();
  const [hero, setHero] = useState(content.hero);
  const [contact, setContact] = useState(content.contact);
  const [team, setTeam] = useState(content.team);
  const [pages, setPages] = useState(content.pages || {});
  const [insights, setInsights] = useState(content.insights || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => { document.title = "Site Editor | norse3"; }, []);
  useEffect(() => { setHero(content.hero); setContact(content.contact); setTeam(content.team); setPages(content.pages || {}); setInsights(content.insights || []); }, [content]);

  const save = async () => {
    setSaving(true);
    try {
      await authApi.put("/cms/content", { hero, contact, team, pages, insights });
      await refresh();
      toast.success("Site content updated");
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset all editable content to defaults?")) return;
    try { await authApi.post("/cms/reset"); await refresh(); toast.success("Reset to defaults"); }
    catch (e) { toast.error("Reset failed"); }
  };

  const setMember = (i, k, v) => setTeam((t) => t.map((m, idx) => (idx === i ? { ...m, [k]: v } : m)));
  const addMember = () => setTeam((t) => [...t, { name: "", role: "", initials: "", linkedin: "", photo: "" }]);
  const removeMember = (i) => setTeam((t) => t.filter((_, idx) => idx !== i));

  const setPage = (key, field, v) => setPages((p) => ({ ...p, [key]: { ...(p[key] || {}), [field]: v } }));
  const setArticle = (i, k, v) => setInsights((a) => a.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));

  return (
    <div className="min-h-screen pt-16 bg-navy-950">
      <div className="mx-auto max-w-4xl px-5 lg:px-8 py-10">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Admin</Link>
            <h1 className="mt-2 font-heading text-2xl font-bold text-foreground">Site editor</h1>
            <p className="text-sm text-muted-foreground">Edit key public content. Changes are saved to the database and appear live.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={reset} className="border-white/15 bg-transparent hover:bg-white/5 text-foreground"><RotateCcw className="mr-1.5 h-4 w-4" /> Reset</Button>
            <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90">{saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Save changes</Button>
          </div>
        </div>

        {/* Hero */}
        <Card title="Homepage hero">
          <F label="Eyebrow"><Input value={hero?.eyebrow || ""} onChange={(e) => setHero({ ...hero, eyebrow: e.target.value })} className="bg-navy-800 border-white/12" /></F>
          <F label="Title"><Textarea rows={2} value={hero?.title || ""} onChange={(e) => setHero({ ...hero, title: e.target.value })} className="bg-navy-800 border-white/12 resize-none" /></F>
          <F label="Subtitle"><Textarea rows={3} value={hero?.subtitle || ""} onChange={(e) => setHero({ ...hero, subtitle: e.target.value })} className="bg-navy-800 border-white/12 resize-none" /></F>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Primary CTA"><Input value={hero?.primaryCta || ""} onChange={(e) => setHero({ ...hero, primaryCta: e.target.value })} className="bg-navy-800 border-white/12" /></F>
            <F label="Secondary CTA"><Input value={hero?.secondaryCta || ""} onChange={(e) => setHero({ ...hero, secondaryCta: e.target.value })} className="bg-navy-800 border-white/12" /></F>
          </div>
        </Card>

        {/* Contact */}
        <Card title="Contact">
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Email"><Input value={contact?.email || ""} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="bg-navy-800 border-white/12" /></F>
            <F label="Location"><Input value={contact?.location || ""} onChange={(e) => setContact({ ...contact, location: e.target.value })} className="bg-navy-800 border-white/12" /></F>
          </div>
        </Card>

        {/* Page heros */}
        <Card title="Page headers (Platform, Lögberg, Insights)">
          <div className="space-y-5">
            {[["platform", "Platform"], ["logberg", "Lögberg"], ["insights", "Insights"]].map(([key, label]) => (
              <div key={key} className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-3">
                <p className="text-sm font-semibold text-foreground">{label}</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input placeholder="Eyebrow" value={pages[key]?.eyebrow || ""} onChange={(e) => setPage(key, "eyebrow", e.target.value)} className="bg-navy-800 border-white/12" />
                  <Input placeholder="Title" value={pages[key]?.title || ""} onChange={(e) => setPage(key, "title", e.target.value)} className="bg-navy-800 border-white/12" />
                </div>
                <Textarea rows={2} placeholder="Subtitle" value={pages[key]?.subtitle || ""} onChange={(e) => setPage(key, "subtitle", e.target.value)} className="bg-navy-800 border-white/12 resize-none" />
              </div>
            ))}
          </div>
        </Card>

        {/* Insights articles */}
        <Card title="Insights articles">
          <div className="space-y-4">
            {(insights || []).map((a, i) => (
              <div key={a.slug || `article-${i}`} className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input placeholder="Title" value={a.title || ""} onChange={(e) => setArticle(i, "title", e.target.value)} className="bg-navy-800 border-white/12 sm:col-span-2" />
                  <Input placeholder="Category" value={a.category || ""} onChange={(e) => setArticle(i, "category", e.target.value)} className="bg-navy-800 border-white/12" />
                  <Input placeholder="Read time" value={a.readTime || ""} onChange={(e) => setArticle(i, "readTime", e.target.value)} className="bg-navy-800 border-white/12" />
                </div>
                <Textarea rows={2} placeholder="Excerpt" value={a.excerpt || ""} onChange={(e) => setArticle(i, "excerpt", e.target.value)} className="bg-navy-800 border-white/12 resize-none" />
                <div>
                  <Label className="text-xs text-muted-foreground">Body (separate paragraphs with a blank line)</Label>
                  <Textarea rows={6} value={Array.isArray(a.body) ? a.body.join("\n\n") : (a.body || "")} onChange={(e) => setArticle(i, "body", e.target.value.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean))} className="mt-1 bg-navy-800 border-white/12 resize-none" />
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Team */}
        <Card title="Founding team" action={<Button size="sm" variant="outline" onClick={addMember} className="border-white/15 bg-transparent hover:bg-white/5 text-foreground"><Plus className="mr-1 h-3.5 w-3.5" /> Add</Button>}>
          <div className="space-y-4">
            {(team || []).map((m, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                <div className="flex items-start gap-4">
                  {m.photo ? <img src={m.photo} alt={m.name || "Team member photo"} className="h-14 w-14 rounded-full object-cover ring-1 ring-white/10" /> : <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/12 text-primary text-sm"><ImageIcon className="h-5 w-5" /></span>}
                  <div className="flex-1 grid sm:grid-cols-2 gap-3">
                    <Input placeholder="Name" value={m.name} onChange={(e) => setMember(i, "name", e.target.value)} className="bg-navy-800 border-white/12" />
                    <Input placeholder="Role" value={m.role} onChange={(e) => setMember(i, "role", e.target.value)} className="bg-navy-800 border-white/12" />
                    <Input placeholder="Initials" value={m.initials} onChange={(e) => setMember(i, "initials", e.target.value)} className="bg-navy-800 border-white/12" />
                    <Input placeholder="LinkedIn URL" value={m.linkedin} onChange={(e) => setMember(i, "linkedin", e.target.value)} className="bg-navy-800 border-white/12" />
                    <Input placeholder="Photo URL" value={m.photo} onChange={(e) => setMember(i, "photo", e.target.value)} className="bg-navy-800 border-white/12 sm:col-span-2" />
                    <Textarea placeholder="Background (1–2 sentences, shown when visitors tap this founder on the home page)" value={m.bio || ""} onChange={(e) => setMember(i, "bio", e.target.value)} rows={3} className="bg-navy-800 border-white/12 sm:col-span-2" data-testid={`editor-team-bio-${i}`} />
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => removeMember(i)} className="text-signal-red hover:bg-signal-red/10"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Card({ title, action, children }) {
  return (
    <div className="mt-6 rounded-lg border border-white/10 bg-navy-900/60 p-6">
      <div className="flex items-center justify-between"><h2 className="text-lg font-semibold text-foreground">{title}</h2>{action}</div>
      <div className="mt-4 space-y-4">{children}</div>
    </div>
  );
}
function F({ label, children }) { return <div><Label className="text-sm text-foreground">{label}</Label><div className="mt-1.5">{children}</div></div>; }
